import { useNavigate } from "react-router-dom";
import { BadgeCheck } from "lucide-react";
import { motion } from "framer-motion";
import { formatINR, imgUrl } from "@/lib/format";
import { V_ICONS } from "@/lib/verticalIcons";

export default function ListingCard({ listing, config, index = 0 }) {
  const navigate = useNavigate();
  const specs = listing.specs || {};
  const badge = config.badgeKey ? specs[config.badgeKey] : null;
  const priceSuffix = config.priceSuffix ? config.priceSuffix(specs) : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      onClick={() => navigate(`/v/${config.slug}/${listing.id}`)}
      data-testid={`listing-card-${listing.id}`}
      className="group cursor-pointer bg-white border border-slate-200 rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:border-navy/20 transition-all duration-300"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={imgUrl(listing.images?.[0])} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {badge && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-xs font-semibold uppercase tracking-wide bg-navy text-white">
            {badge}
          </span>
        )}
      </div>
      <div className="p-5">
        <p className="font-display text-2xl font-bold text-slate-900">
          {formatINR(listing.price)}<span className="text-sm font-medium text-slate-400">{priceSuffix}</span>
        </p>
        <h3 className="mt-1.5 font-medium text-slate-900 line-clamp-1">{listing.title}</h3>
        <div className="mt-1 flex items-center justify-between">
          <p className="text-sm text-slate-500">{listing.city}</p>
          {listing.owner_verified && (
            <span data-testid={`listing-verified-${listing.id}`} className="flex items-center gap-1 text-xs font-semibold text-navy bg-navy/10 px-2 py-0.5 rounded-full">
              <BadgeCheck className="h-3.5 w-3.5" /> Verified
            </span>
          )}
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-y-2 text-sm text-slate-600">
          {config.cardSpecs.map((sp, i) => {
            const Icon = V_ICONS[sp.icon];
            return (
              <span key={i} className="flex items-center gap-1.5 truncate">
                <Icon className="h-4 w-4 text-gold-dark shrink-0" /> <span className="truncate">{sp.format(specs)}</span>
              </span>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}
